import { Component } from '@angular/core';
import { BaseComponent } from '../../shared/components/component-base';
import { DashboardService } from './services/dashboard.service';
import { MeetingService } from '../association/services/meeting.service';
import { MeetingsSummaryDto } from '../association/models/meeting-models';
import { ApiResponse } from '../../shared/models/api-response.model';
import { DashboardDto } from './models/dashboard-dto';
import { ApexNonAxisChartSeries, ApexChart, ApexPlotOptions, ApexFill, ApexStroke } from 'ng-apexcharts';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent extends BaseComponent {
  dashboardDto: DashboardDto = {} as DashboardDto;
  meetingsSummary: MeetingsSummaryDto | null = null;

  // Tooltip configuration for column charts
  public columnChartTooltip: any = {
    theme: 'dark',
    x: { show: true },
    y: {
      formatter: function (val: number) {
        return val.toString();
      }
    }
  };

  // Apex chart configs (small radial used in agreement-details)
  public chartSmall: ApexChart = { type: 'radialBar', height: 80, offsetY: 0, redrawOnParentResize: true };
  public plotOptionsSmall: ApexPlotOptions = {
    radialBar: {
      startAngle: -135,
      endAngle: 135,
      hollow: { size: '65%', background: '#f0f8f8' },
      track: { background: '#e9f6f3', strokeWidth: '100%', opacity: 0.4 },
      dataLabels: {
        name: { show: false },
        value: {
          fontSize: '14px',
          fontWeight: 600,
          color: '#2c5f5f',
          offsetY: 6,
          formatter: function (val: number) { return val + '%'; }
        }
      }
    }
  };
  public fillInfo: ApexFill = { colors: ['#007AFF'] };
  public fillSuccess: ApexFill = { colors: ['#23A566'] };
  public fillDanger: ApexFill = { colors: ['#FF3B30'] };
  public fillWarning: ApexFill = { colors: ['#FF9500'] };
  public stroke: ApexStroke = { lineCap: 'round' };
  public labels: string[] = ['Progress'];

  // Unique configurations for each task chart to prevent interference
  public chartSmallInProgress: ApexChart = { type: 'radialBar', height: 80, offsetY: 0, redrawOnParentResize: true };
  public plotOptionsSmallInProgress: ApexPlotOptions = {
    radialBar: {
      startAngle: -135,
      endAngle: 135,
      track: { background: '#f1f6f6', strokeWidth: '97%' },
      dataLabels: { show: false }
    }
  };
  public strokeInProgress: ApexStroke = { lineCap: 'round' };
  public labelsInProgress: string[] = ['Progress'];

  public chartSmallCompleted: ApexChart = { type: 'radialBar', height: 80, offsetY: 0, redrawOnParentResize: true };
  public plotOptionsSmallCompleted: ApexPlotOptions = {
    radialBar: {
      startAngle: -135,
      endAngle: 135,
      track: { background: '#f1f6f6', strokeWidth: '97%' },
      dataLabels: { show: false }
    }
  };
  public strokeCompleted: ApexStroke = { lineCap: 'round' };
  public labelsCompleted: string[] = ['Progress'];

  public chartSmallDelayed: ApexChart = { type: 'radialBar', height: 80, offsetY: 0, redrawOnParentResize: true };
  public plotOptionsSmallDelayed: ApexPlotOptions = {
    radialBar: {
      startAngle: -135,
      endAngle: 135,
      track: { background: '#f1f6f6', strokeWidth: '97%' },
      dataLabels: { show: false }
    }
  };
  public strokeDelayed: ApexStroke = { lineCap: 'round' };
  public labelsDelayed: string[] = ['Progress'];

  public chartSmallPending: ApexChart = { type: 'radialBar', height: 80, offsetY: 0, redrawOnParentResize: true };
  public plotOptionsSmallPending: ApexPlotOptions = {
    radialBar: {
      startAngle: -135,
      endAngle: 135,
      track: { background: '#f1f6f6', strokeWidth: '97%' },
      dataLabels: { show: false }
    }
  };
  public strokePending: ApexStroke = { lineCap: 'round' };
  public labelsPending: string[] = ['Progress'];

  // Computed percentages
  get tasksTotal(): number {
    const dto = this.dashboardDto || ({} as DashboardDto);
    const vals = [dto.tasksInProgress || 0, dto.tasksPending || 0, dto.tasksDelayed || 0, dto.tasksCompleted || 0];
    return vals.reduce((s, v) => s + v, 0) || 0;
  }

  private percent(value: number): number {
    const total = this.tasksTotal || 0;
    if (!total) return 0;
    return Math.round((value / total) * 100);
  }

  get tasksInProgressPercent(): number { return this.percent(this.dashboardDto?.tasksInProgress || 0); }
  get tasksPendingPercent(): number { return this.percent(this.dashboardDto?.tasksPending || 0); }
  get tasksDelayedPercent(): number { return this.percent(this.dashboardDto?.tasksDelayed || 0); }
  get tasksCompletedPercent(): number { return this.percent(this.dashboardDto?.tasksCompleted || 0); }

  // --- Partners charts (static data) ---
  public partnersDonutChart: ApexChart = { type: 'donut', height: 220, redrawOnParentResize: true };
  public partnersDonutPlotOptions: ApexPlotOptions = { pie: { donut: { size: '65%', labels: { show: true } } } };
  public partnersDonutFill: ApexFill = { colors: ['#36B0C9', '#23A566', '#FFD166', '#9B9B9B', '#A78BFA'] };

  // Labels shown under donut (will be populated from API or use translations)
  public partnersDonutLabels: string[] = [];

  // Stable donut series (populated from API)
  public partnersDonutSeriesStable: ApexNonAxisChartSeries = [0, 0, 0, 0, 0];

  public partnersColumnPlotOptions: ApexPlotOptions = { bar: { horizontal: false, columnWidth: '48%', borderRadius: 6 } };
  public partnersColumnFill: ApexFill = { colors: ['#36B0C9'] };
  public partnersColumnDataLabels: any = { enabled: false };
  public partnersColumnYAxis: any = { labels: { style: { colors: ['#7a8a8a'] } }, min: 0, tickAmount: 5 };
  public partnersColumnGrid: any = { borderColor: '#f1f6f6', strokeDashArray: 4, yaxis: { lines: { show: true } } };
  public partnersColumnChart: ApexChart = { type: 'bar', height: 350, width: '100%', toolbar: { show: false }, redrawOnParentResize: true };
  public partnersColumnXAxis: any = { categories: [] };

  // Partners monthly series (populated from API)
  public partnersMonthlySeriesStable: any[] = [{ name: 'Partners', data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] }];

  // --- Donations charts (dynamic data from API) ---
  public donationsDonutChart: ApexChart = { type: 'donut', height: 220, redrawOnParentResize: true };
  public donationsDonutPlotOptions: ApexPlotOptions = { pie: { donut: { size: '65%', labels: { show: true } } } };
  public donationsDonutFill: ApexFill = { colors: ['#36B0C9', '#2c5f5f', '#FFD166', '#A78BFA', '#23A566'] };
  public donationsDonutLabels: string[] = [];
  // Donations donut series (populated from API)
  public donationsDonutSeriesStable: ApexNonAxisChartSeries = [50, 50];

  public donationsColumnChart: ApexChart = { type: 'bar', height: 320, toolbar: { show: false }, redrawOnParentResize: true };
  // use same months as partners
  public donationsColumnXAxis: any = { categories: [] };
  public donationsColumnFill: ApexFill = { colors: ['#36B0C9', '#2c5f5f'] };
  public donationsColumnPlotOptions: ApexPlotOptions = { bar: { horizontal: false, columnWidth: '48%', borderRadius: 6 } };

  // Monthly donations series (populated from API)
  public donationsMonthlySeriesStable: any[] = [
    { name: 'Cash', data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
    { name: 'In-Kind', data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] }
  ];

  // Small radial chart series (tasks) - stable arrays (populated from API)
  public tasksInProgressSeries: any[] = [0];
  public tasksCompletedSeries: any[] = [0];
  public tasksDelayedSeries: any[] = [0];
  public tasksPendingSeries: any[] = [0];

  // --- Events charts ---
  public eventsColumnChart: ApexChart = { type: 'bar', height: 280, toolbar: { show: false }, redrawOnParentResize: true };
  public eventsColumnPlotOptions: ApexPlotOptions = { bar: { horizontal: false, columnWidth: '50%', borderRadius: 8 } };
  public eventsColumnFill: ApexFill = { colors: ['#A78BFA', '#36B0C9', '#23A566'] };
  public eventsColumnXAxis: any = { categories: [] };
  public eventsMonthlySeriesStable: any[] = [{ name: 'Events', data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] }];

  // Events by type - horizontal bar chart (better for many categories with Arabic labels)
  public eventsByTypeChart: ApexChart = { type: 'bar', height: 320, toolbar: { show: false }, redrawOnParentResize: true };
  public eventsByTypePlotOptions: ApexPlotOptions = { bar: { horizontal: true, columnWidth: '60%', borderRadius: 6 } };
  public eventsByTypeFill: ApexFill = { colors: ['#A78BFA', '#36B0C9', '#FFD166', '#23A566', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'] };
  public eventsByTypeXAxis: any = { categories: [] };
  public eventsByTypeYAxis: any = { labels: { style: { colors: ['#2c5f5f'], fontSize: '12px' } } };
  public eventsByTypeDataLabels: any = { enabled: true, style: { colors: ['#fff'] } };
  public eventsByTypeGrid: any = { borderColor: '#f1f6f6', strokeDashArray: 4, xaxis: { lines: { show: true } } };
  public eventsByTypeSeriesStable: any[] = [{ name: 'العدد', data: [0, 0, 0, 0, 0, 0, 0, 0] }];

  // --- Volunteers charts ---
  public volunteersAreaChart: ApexChart = { type: 'area', height: 280, toolbar: { show: false }, redrawOnParentResize: true };
  public volunteersAreaFill: ApexFill = { colors: ['#36B0C9'], type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.1 } };
  public volunteersAreaStroke: ApexStroke = { curve: 'smooth', width: 2 };
  public volunteersAreaXAxis: any = { categories: [] };
  public volunteersMonthlySeriesStable: any[] = [{ name: 'Volunteers', data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] }];

  public volunteersPieChart: ApexChart = { type: 'pie', height: 200, redrawOnParentResize: true };
  public volunteersPiePlotOptions: ApexPlotOptions = { pie: { donut: { size: '0%' } } };
  public volunteersPieFill: ApexFill = { colors: ['#23A566', '#FFD166', '#9B9B9B'] };
  public volunteersPieLabels: string[] = [];

  // --- Certificates charts ---
  public certificatesLineChart: ApexChart = { type: 'line', height: 250, toolbar: { show: false }, redrawOnParentResize: true };
  public certificatesLineStroke: ApexStroke = { curve: 'smooth', width: 3 };
  public certificatesLineFill: ApexFill = { colors: ['#23A566'], type: 'solid' };
  public certificatesLineXAxis: any = { categories: [] };
  public certificatesMonthlySeriesStable: any[] = [{ name: 'Certificates', data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] }];

  // --- Coupons charts ---
  public couponsRadialChart: ApexChart = { type: 'radialBar', height: 200, redrawOnParentResize: true };
  public couponsRadialPlotOptions: ApexPlotOptions = {
    radialBar: {
      startAngle: -90,
      endAngle: 90,
      track: { background: '#e9f6f3', strokeWidth: '97%', margin: 5 },
      dataLabels: {
        name: { show: true, fontSize: '14px', fontWeight: 600 },
        value: { show: true, fontSize: '16px', fontWeight: 700, color: '#2c5f5f' }
      }
    }
  };
  public couponsRadialFill: ApexFill = { colors: ['#FF9500', '#23A566', '#36B0C9'] };
  public couponsRadialLabels: string[] = [];
  public couponsRadialSeriesStable: ApexNonAxisChartSeries = [0, 0, 0];

  // --- Initiatives charts ---
  public initiativesProgressChart: ApexChart = { type: 'bar', height: 250, toolbar: { show: false }, redrawOnParentResize: true };
  public initiativesProgressPlotOptions: ApexPlotOptions = { bar: { horizontal: true, columnWidth: '50%', borderRadius: 8, barHeight: '60%' } };
  public initiativesProgressFill: ApexFill = { colors: ['#36B0C9', '#23A566', '#FFD166', '#A78BFA'] };
  public initiativesProgressSeriesStable: any[] = [{ name: 'Progress', data: [0, 0, 0, 0, 0] }];

  // --- Campaigns charts ---
  public campaignsGaugeChart: ApexChart = { type: 'radialBar', height: 220, redrawOnParentResize: true };
  public campaignsGaugePlotOptions: ApexPlotOptions = {
    radialBar: {
      startAngle: -135,
      endAngle: 225,
      hollow: { margin: 5, size: '30%', background: 'transparent' },
      track: { background: '#f1f6f6', strokeWidth: '97%' },
      dataLabels: {
        name: { show: false },
        value: { show: true, fontSize: '22px', fontWeight: 700, color: '#2c5f5f', formatter: function (val: number) { return val + '%' } }
      }
    }
  };
  public campaignsGaugeFill: ApexFill = { colors: ['#23A566', '#FFD166', '#FF3B30'] };
  public campaignsGaugeSeriesStable: ApexNonAxisChartSeries = [0, 0, 0];

  // --- Permits charts ---
  public permitsColumnChart: ApexChart = { type: 'bar', height: 250, toolbar: { show: false }, redrawOnParentResize: true };
  public permitsColumnPlotOptions: ApexPlotOptions = { bar: { horizontal: false, columnWidth: '45%', borderRadius: 6 } };
  public permitsColumnFill: ApexFill = { colors: ['#23A566', '#FF9500', '#FF3B30'] };
  public permitsColumnXAxis: any = { categories: [] };
  public permitsMonthlySeriesStable: any[] = [
    { name: 'Approved', data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
    { name: 'Pending', data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
    { name: 'Rejected', data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] }
  ];

  constructor(
    private dashboardService: DashboardService,
    private meetingService: MeetingService,
    public translate: TranslateService) {
    super()
    if (!this.isAuthenticated())
      this._router.navigate(['/login']);
    this.initializeChartLabels();
    this.getDashboard();
    this.getMeetingsSummary();
    this.updateTooltipLabels();
  }

  private initializeChartLabels() {
    // Get month translations and initialize chart axes
    this.translate.get([
      'common.months.Jan', 'common.months.Feb', 'common.months.Mar', 'common.months.Apr',
      'common.months.May', 'common.months.Jun', 'common.months.Jul', 'common.months.Aug',
      'common.months.Sep', 'common.months.Oct', 'common.months.Nov', 'common.months.Dec'
    ]).subscribe(months => {
      const monthArray = [
        months['common.months.Jan'], months['common.months.Feb'], months['common.months.Mar'],
        months['common.months.Apr'], months['common.months.May'], months['common.months.Jun'],
        months['common.months.Jul'], months['common.months.Aug'], months['common.months.Sep'],
        months['common.months.Oct'], months['common.months.Nov'], months['common.months.Dec']
      ];

      this.partnersColumnXAxis = { categories: monthArray };
      this.donationsColumnXAxis = { categories: monthArray };
      this.eventsColumnXAxis = { categories: monthArray };
      this.volunteersAreaXAxis = { categories: monthArray };
      this.certificatesLineXAxis = { categories: monthArray };
      this.permitsColumnXAxis = { categories: monthArray };

      // Force chart refresh after updating axis categories
      this.refreshCharts();
    });

    // Initialize donut/pie labels with translations (if not loaded from API)
    if (this.volunteersPieLabels.length === 0) {
      this.translate.get(['common.status_active', 'partner.isVIP', 'dashboard.inactive']).subscribe(translations => {
        this.volunteersPieLabels = [
          translations['common.status_active'],
          translations['partner.isVIP'],
          translations['dashboard.inactive']
        ];
      });
    }
  }

  private refreshCharts() {
    // Create new object references to trigger Angular change detection and chart redraw
    this.partnersColumnChart = { ...this.partnersColumnChart };
    this.donationsColumnChart = { ...this.donationsColumnChart };
    this.eventsColumnChart = { ...this.eventsColumnChart };
    this.volunteersAreaChart = { ...this.volunteersAreaChart };
    this.certificatesLineChart = { ...this.certificatesLineChart };
    this.permitsColumnChart = { ...this.permitsColumnChart };
  }

  /**
   * Selects localized labels (nameEn or nameAr) based on current language.
   * Expects items with nameAr and nameEn properties.
   */
  private getLocalizedLabels(items: any[]): string[] {
    if (!Array.isArray(items)) return [];
    const isEnglish = this.translate.currentLang === 'en';
    return items.map(item => {
      if (typeof item === 'string') return item; // Fallback if item is a string
      return isEnglish ? (item.nameEn || item.nameAr || '') : (item.nameAr || item.nameEn || '');
    });
  }

  updateTooltipLabels() {
    this.columnChartTooltip = {
      theme: 'dark',
      x: { show: true },
      y: {
        formatter: function (val: number) {
          return val.toString();
        }
      }
    };
  }
  getDashboard() {
    this.dashboardService.getAgreementDashboard().subscribe({
      next: (res: ApiResponse<DashboardDto>) => {
        this.dashboardDto = res.value;
        this.dashboardDto.lastUpdate = new Date();
        // update small radial series so charts reflect loaded DTO
        this.tasksInProgressSeries = [this.tasksInProgressPercent];
        this.tasksCompletedSeries = [this.tasksCompletedPercent];
        this.tasksDelayedSeries = [this.tasksDelayedPercent];
        this.tasksPendingSeries = [this.tasksPendingPercent];
        const v = (res.value as any) || {};

        // Partners monthly (expecting array of 12 numbers)
        if (Array.isArray(v.partnersMonthly) && v.partnersMonthly.length === 12) {
          this.partnersMonthlySeriesStable = [{ name: 'Partners', data: v.partnersMonthly }];
          // Trigger chart redraw after data loads to ensure proper width
          setTimeout(() => {
            this.partnersColumnChart = { ...this.partnersColumnChart };
          }, 100);
        }

        // Partners donut (if API provides explicit breakdown)
        if (Array.isArray(v.partnersDonut) && v.partnersDonut.length) {
          this.partnersDonutSeriesStable = v.partnersDonut;
        }

        // Partners donut labels (if API provides dynamic labels)
        if (Array.isArray(v.partnersDonutLabels) && v.partnersDonutLabels.length) {
          // Localize labels based on current language (use nameEn/nameAr if available, otherwise use raw labels)
          this.partnersDonutLabels = this.getLocalizedLabels(v.partnersDonutLabels);
          // Ensure series array length matches labels length
          if (this.partnersDonutSeriesStable.length !== this.partnersDonutLabels.length) {
            this.partnersDonutSeriesStable = new Array(this.partnersDonutLabels.length).fill(0);
          }
        }

        // Donations monthly (expecting array of 12 numbers)
        if (Array.isArray(v.donationsMonthly) && v.donationsMonthly.length === 12) {
          this.donationsMonthlySeriesStable = [{ name: 'Donations', data: v.donationsMonthly }];
        }

        // Donations breakdown for donut chart (dynamic donation types from database)
        if (Array.isArray(v.donationsBreakdown) && v.donationsBreakdown.length > 0) {
          this.donationsDonutSeriesStable = v.donationsBreakdown;
        }

        // Donation type labels (Arabic names from database)
        if (Array.isArray(v.donationsBreakdownLabels) && v.donationsBreakdownLabels.length > 0) {
          // Localize labels based on current language (use nameEn/nameAr if available, otherwise use raw labels)
          this.donationsDonutLabels = this.getLocalizedLabels(v.donationsBreakdownLabels);
        }

        // Events monthly
        if (Array.isArray(v.eventsMonthly) && v.eventsMonthly.length === 12) {
          this.eventsMonthlySeriesStable = [{ name: 'Events', data: v.eventsMonthly }];
        }

        // Events by type - horizontal bar chart
        if (Array.isArray(v.eventsByType) && v.eventsByType.length > 0) {
          // Use translated label for series name instead of hard-coded Arabic
          const seriesName = this.translate.instant('common.count');
          this.eventsByTypeSeriesStable = [{ name: seriesName, data: v.eventsByType }];
        }

        // Event type labels from API
        if (Array.isArray(v.eventsByTypeLabels) && v.eventsByTypeLabels.length > 0) {
          // Localize category labels based on current language (use nameEn/nameAr if available, otherwise use raw labels)
          const localizedCategories = this.getLocalizedLabels(v.eventsByTypeLabels);
          this.eventsByTypeXAxis = { categories: localizedCategories };
        }

        // Volunteers monthly
        if (Array.isArray(v.volunteersMonthly) && v.volunteersMonthly.length === 12) {
          this.volunteersMonthlySeriesStable = [{ name: 'Volunteers', data: v.volunteersMonthly }];
        }

        // Certificates monthly
        if (Array.isArray(v.certificatesMonthly) && v.certificatesMonthly.length === 12) {
          this.certificatesMonthlySeriesStable = [{ name: 'Certificates', data: v.certificatesMonthly }];
        }

        // Coupons radial (distributed, redeemed, available)
        const totalCoupons = Number(v.totalCoupons || 0);
        const distributed = Number(v.distributedCoupons || 0);
        const redeemed = Number(v.redeemedCoupons || 0);
        const available = totalCoupons - distributed;
        if (totalCoupons > 0) {
          const distPct = Math.round((distributed / totalCoupons) * 100);
          const redPct = Math.round((redeemed / totalCoupons) * 100);
          const availPct = 100 - distPct - redPct;
          this.couponsRadialSeriesStable = [distPct, redPct, availPct];
        }

        // Initiatives progress
        if (Array.isArray(v.initiativesProgress) && v.initiativesProgress.length) {
          this.initiativesProgressSeriesStable = [{ name: 'Progress', data: v.initiativesProgress }];
        }

        // Campaigns gauge (active, completed, pending)
        const totalCampaigns = Number(v.totalCampaigns || 0);
        const activeCampaigns = Number(v.activeCampaigns || 0);
        const completedCampaigns = Number(v.completedCampaigns || 0);
        if (totalCampaigns > 0) {
          const activePct = Math.round((activeCampaigns / totalCampaigns) * 100);
          const completedPct = Math.round((completedCampaigns / totalCampaigns) * 100);
          const pendingPct = 100 - activePct - completedPct;
          this.campaignsGaugeSeriesStable = [activePct, completedPct, pendingPct];
        }

        // Permits monthly (approved, pending, rejected)
        if (Array.isArray(v.permitsMonthly) && v.permitsMonthly.length === 12) {
          this.permitsMonthlySeriesStable = [
            { name: 'Approved', data: v.permitsMonthly },
            { name: 'Pending', data: new Array(12).fill(0) },
            { name: 'Rejected', data: new Array(12).fill(0) }
          ];
        }
      }
    });
  }

  getMeetingsSummary(topN: number = 5) {
    this.meetingService.getSummary(topN).subscribe({
      next: (res) => {
        this.meetingsSummary = res.value;
      }
    });
  }


}
